import { Directive, Input, OnInit, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges, forwardRef, Injector } from '@angular/core';
import { ValidationErrors, FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

/**
 * Abstract base class for custom form control components.
 * Provides common functionality for handling form control integration,
 * validation state, error messages, and interaction tracking.
 * Implements ControlValueAccessor to integrate with Angular forms.
 */
@Directive({
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => BaseControlComponent),
			multi: true
		}
	]
})
export abstract class BaseControlComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
	/**
	 * The name of the form control, used for linking label/input and potentially for error messages.
	 * @type {string}
	 */
	@Input() controlName: string;
	/**
	 * The display label for the form control. Defaults to 'Label'.
	 * @type {string}
	 */
	@Input() label: string = 'Label';
	/**
	 * Indicates if the control is required. Used for displaying the required marker (*).
	 * @type {boolean}
	 */
	@Input() required: boolean = false;
	/**
	 * Flag indicating if the parent form has been submitted. Used for validation display logic.
	 * @type {boolean}
	 */
	@Input() submitted: boolean = false;
	/**
	 * Enables debug logging to the console for this component instance.
	 * @type {boolean}
	 */
	@Input() debug: boolean = false;
	/**
	 * If true, validation errors are shown immediately when the control becomes invalid and touched/dirty,
	 * regardless of the parent form's submitted state.
	 * @type {boolean}
	 */
	@Input() validateImmediately: boolean = false;

	/**
	 * @private
	 */
	private _internalControl: FormControl | null = null;

	/**
	 * Public getter for the associated FormControl instance.
	 * @type {(FormControl | null)}
	 */
	public get control(): FormControl | null {
		if (!this._internalControl && this.ngControl && this.ngControl.control instanceof FormControl) {
			this._internalControl = this.ngControl.control;
			if (!this.controlReady) {
				this.controlReady = true;
				this.onControlReady();
			}
		}
		return this._internalControl;
	}

	/**
	 * Public property reflecting the current invalid state for template binding.
	 * @type {boolean}
	 */
	public isCurrentlyInvalid: boolean = false;
	/**
	 * Public property holding the current error message for template binding.
	 * @type {string}
	 */
	public currentErrorMessage: string = '';

	/**
	 * Subscription to the parent control's valueChanges observable.
	 * @type {(Subscription | null)}
	 */
	public parentValueSub: Subscription | null = null;

	/**
	 * Subject used to automatically unsubscribe from observables when the component is destroyed.
	 * @protected
	 */
	protected destroy$ = new Subject<void>();

	/**
	 * @private
	 */
	private controlReady = false;

	/**
	 * @protected
	 */
	protected _value: any = null;
	/**
	 * @protected
	 */
	protected onChange: (value: any) => void = () => {};
	/**
	 * @protected
	 */
	protected onTouched: () => void = () => {};
	/**
	 * @protected
	 */
	protected _disabled: boolean = false;

	/**
	 * @protected
	 */
	protected ngControl: NgControl | null = null;

	/**
	 * Injects required services and attempts to get NgControl.
	 * @param {Injector} injector
	 * @param {TranslateService} translate
	 * @param {ChangeDetectorRef} cdr
	 * @param {MessageService} messageService
	 */
	constructor(
		protected injector: Injector,
		protected translate: TranslateService,
		protected cdr: ChangeDetectorRef,
		protected messageService: MessageService
	) {
		try {
			this.ngControl = this.injector.get(NgControl);
			if (this.ngControl) {
				this.ngControl.valueAccessor = this;
			}
		} catch (err) {
			 this.ngControl = null;
		}
	}

	/**
	 * @override
	 */
	ngOnInit(): void {
		if (!this._internalControl && this.ngControl && this.ngControl.control instanceof FormControl) {
			this._internalControl = this.ngControl.control;
			 if (!this.controlReady) {
				this.controlReady = true;
				this.onControlReady();
			 }
		}
		if (this.controlReady) {
			this._updateDisplayValidationState();
		}
	}

	/**
	 * @param {SimpleChanges} changes
	 * @override
	 */
	ngOnChanges(changes: SimpleChanges): void {
		 if (!this._internalControl && this.ngControl && this.ngControl.control instanceof FormControl) {
			this._internalControl = this.ngControl.control;
			 if (!this.controlReady) {
				this.controlReady = true;
				this.onControlReady();
			 }
		 }
		 if (changes['submitted'] || changes['validateImmediately'] || changes['required'] || changes['label']) {
			this._updateDisplayValidationState();
		 }
	}

	/**
	 * @protected
	 */
	protected onControlReady(): void {
		this.control?.statusChanges
			.pipe(
				distinctUntilChanged(),
				takeUntil(this.destroy$)
			)
			.subscribe(status => {
				this._updateDisplayValidationState();
			});

		Promise.resolve().then(() => {
			this._updateDisplayValidationState();
		});
	}

	/**
	 * @override
	 */
	ngOnDestroy(): void {
		if (this.parentValueSub) {
			this.parentValueSub.unsubscribe();
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * @private
	 * @returns {boolean}
	 */
	private _calculateIsInvalid(): boolean {
		const ctrl = this.control;
		if (!ctrl) { return false; }
		const isEmpty = ctrl.value === null || ctrl.value === '' || typeof ctrl.value === 'undefined';
		const isRequiredInvalid = this.required && isEmpty;
		const isInvalid = ctrl.invalid || isRequiredInvalid;
		const isTouched = ctrl.touched;
		const isDirty = ctrl.dirty;
		let result: boolean;
		if (this.validateImmediately) {
			result = isInvalid && (isTouched || isDirty);
		} else {
			result = isInvalid && (isTouched || this.submitted);
		}
		return result;
	}

	/**
	 * @private
	 * @returns {string}
	 */
	private _calculateErrorMessage(): string {
		const ctrl = this.control;
		const fieldName = this.label || this.controlName;
		const isEmpty = ctrl && (ctrl.value === null || ctrl.value === '' || typeof ctrl.value === 'undefined');

		let translatedField = '';
		if (this.controlName) {
			const key = 'form.field.' + this.controlName;
			const translation = this.translate.instant(key);
			translatedField = (translation !== key) ? translation : (this.label || this.controlName);
		} else {
			translatedField = this.label || '';
		}

		if (this.required && isEmpty) {
			return translatedField + ' ' + this.translate.instant('form.validation.requiredSimple');
		}
		if (!ctrl || !ctrl.errors) { return ''; }
		const errors: ValidationErrors = ctrl.errors;

		if (errors['required']) {
			return translatedField + ' ' + this.translate.instant('form.validation.requiredSimple');
		}
		if (errors['minlength']) {
			return this.translate.instant('form.validation.minLength', { field: fieldName, min: errors['minlength'].requiredLength });
		}
		if (errors['maxlength']) {
			return this.translate.instant('form.validation.maxLength', { field: fieldName, max: errors['maxlength'].requiredLength });
		}
		if (errors['email']) {
			return this.translate.instant('form.validation.email', { field: fieldName });
		}
		if (errors['pattern']) {
			return this.translate.instant('form.validation.pattern', { field: fieldName });
		}

		const firstErrorKey = Object.keys(errors)[0];
		return this.translate.instant('form.validation.invalid', { field: fieldName, error: firstErrorKey });
	}

	/**
	 * @private
	 */
	private _updateDisplayValidationState(): void {
		const newInvalidState = this._calculateIsInvalid();
		const newErrorMessage = newInvalidState ? this._calculateErrorMessage() : '';

		if (this.isCurrentlyInvalid !== newInvalidState || this.currentErrorMessage !== newErrorMessage) {
			this.isCurrentlyInvalid = newInvalidState;
			this.currentErrorMessage = newErrorMessage;
			this.cdr.markForCheck();
		}
	}

	/**
	 * Marks the control as dirty and touched to indicate user interaction.
	 */
	markAsInteracted(): void {
		const ctrl = this.control;
		if (!ctrl) { return; }

		ctrl.markAsDirty();
		ctrl.markAsTouched();
		this.propagateTouched();
		this._updateDisplayValidationState();
		this.cdr.markForCheck();
	}

	/**
	 * Writes a new value to the element.
	 * @param {any} obj
	 * @implements {ControlValueAccessor['writeValue']}
	 */
	writeValue(obj: any): void {
		if (obj !== this._value) {
			this._value = obj;
			this.cdr.markForCheck();
		}
	}

	/**
	 * Registers a callback function that should be called when the control's value changes in the UI.
	 * @param {(value: any) => void} fn
	 * @implements {ControlValueAccessor['registerOnChange']}
	 */
	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	/**
	 * Registers a callback function that should be called when the control receives a blur event.
	 * @param {() => void} fn
	 * @implements {ControlValueAccessor['registerOnTouched']}
	 */
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	/**
	 * This function is called by the Forms module when the control status changes to or from 'DISABLED'.
	 * @param {boolean} isDisabled
	 * @implements {ControlValueAccessor['setDisabledState']}
	 */
	setDisabledState?(isDisabled: boolean): void {
		if (this._disabled !== isDisabled) {
			this._disabled = isDisabled;
			this.cdr.markForCheck();
		}
	}

	/**
	 * Call this method in the child component when the internal value changes
	 * @param {any} value
	 * @protected
	 */
	protected propagateChange(value: any): void {
		if (this.onChange) {
			this.onChange(value);
		}
	}

	/**
	 * Call this method in the child component when the component should be marked as touched (e.g., on blur)
	 * @protected
	 */
	protected propagateTouched(): void {
		if (this.onTouched) {
			this.onTouched();
		}
	}
}
